import React from "react";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import HPIconButton from "@/components/common/IconButton";
import type { DatasetMetric } from "@models/Metric";
import type {
  ScorecardBrand,
  ScorecardDatasetMetricRow,
  ScorecardQuestionRow,
  ScorecardRow,
  ScorecardRowBase,
} from "@/models/Scorecard";
import { isDatasetMetricRow, isQuestionRow } from "@/models/Scorecard";
import { DEFAULT_VISUALIZATION_BASE, getVisualizationBaseSource } from "@models/VisualizationBase";
import { getBaseMappingForBrand } from "@utils/scorecard-fetch-utils";
import { isMetricEligibleForBrandMapping } from "@components/charts/scorecard-config/dataset-metric-row-utils";
import { MetaLabel, TruncatedText, BrandMappingsList } from "./RowListItemShared";

interface RowListItemProps {
  row: ScorecardRow;
  brands: ScorecardBrand[];
  /** Every Question row in the Scorecard — needed to resolve a Question
   *  row's own Scorecard Row Base mapping status per brand (Existing
   *  Question Row Base reads another row's brandMappings). */
  allRows: ScorecardQuestionRow[];
  /** The dataset's saved metrics — used to resolve a Metric row's live
   *  display name/source question; falls back to the row's own cached
   *  datasetMetricName if the metric can no longer be found. */
  datasetMetrics: DatasetMetric[];
  onEdit: () => void;
  onDuplicate?: () => void;
  onDelete: () => void;
}

/** Short summary of a row's Base selection for the list item caption. */
function getBaseSummaryLabel(row: ScorecardQuestionRow): string {
  const base = row.visualizationBase ?? DEFAULT_VISUALIZATION_BASE;
  if (!base.enabled) return "All eligible respondents";
  const source = getVisualizationBaseSource(base);
  if (source === "preconfigured") {
    return base.preconfiguredBaseName
      ? `Pre-configured base: ${base.preconfiguredBaseName}`
      : "Pre-configured base";
  }
  return base.name ? `Custom base: ${base.name}` : "Custom base";
}

/** Short summary of a row's Scorecard Row Base for the list item caption. */
function getRowBaseSummaryLabel(base: ScorecardRowBase): string {
  if (base.kind === "existing_question_row") {
    return base.questionRowLabel;
  }
  return `${base.questionBase.sourceQuestionLabel} (New Question Base)`;
}

const MAX_BASE_TAG_LENGTH = 26;

function truncateLabel(text: string, maxLength: number): string {
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

const RowListItem: React.FC<RowListItemProps> = ({
  row,
  brands,
  allRows,
  datasetMetrics,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const questionRow = isQuestionRow(row) ? row : null;
  const datasetMetricRow: ScorecardDatasetMetricRow | null = isDatasetMetricRow(row) ? row : null;
  const resolvedMetric = datasetMetricRow
    ? (datasetMetrics.find((m) => m.id === datasetMetricRow.datasetMetricId) ?? null)
    : null;
  const metricIneligibility = resolvedMetric
    ? isMetricEligibleForBrandMapping(resolvedMetric)
    : null;
  const brandLabelById = new Map(brands.map((b) => [b.id, b.label]));

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: row.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Handle column width (20px) + the gap next to it (0.5 spacing unit = 4px),
  // reused as the left indent for the rows below so they line up with the
  // row name rather than with the handle itself.
  const CONTENT_INDENT = 3;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        flexDirection: "column",
        pr: 2,
        py: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: row.type === "heading" ? "#f5f5f5" : "transparent",
      }}
    >
      {/* Top row: drag handle + row name + actions, all centered against
          each other via flexbox so the handle stays vertically aligned
          with the name regardless of row height. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 20,
            flexShrink: 0,
            cursor: isDragging ? "grabbing" : "grab",
            color: "#999",
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: "1rem", position: "relative", top: "1px" }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TruncatedText text={row.label} fontWeight={700} />
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          <Tooltip title="Edit row">
            <span>
              <HPIconButton ariaLabel="Edit row" onClick={onEdit}>
                <EditIcon fontSize="small" />
              </HPIconButton>
            </span>
          </Tooltip>
          {(questionRow || datasetMetricRow) && onDuplicate && (
            <Tooltip title="Duplicate row">
              <span>
                <HPIconButton ariaLabel="Duplicate row" onClick={onDuplicate}>
                  <ContentCopyIcon fontSize="small" />
                </HPIconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Delete row">
            <span>
              <HPIconButton ariaLabel="Delete row" onClick={onDelete}>
                <DeleteIcon fontSize="small" />
              </HPIconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {questionRow && (
        <Tooltip title={`${questionRow.sourceQuestionLabel} · ${questionRow.sourceQuestionTitle}`}>
          <Box
            sx={{
              ml: CONTENT_INDENT,
              display: "flex",
              alignItems: "baseline",
              gap: 0.5,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              variant="caption"
              color="text.primary"
              fontWeight={500}
              sx={{ flexShrink: 0 }}
            >
              {questionRow.sourceQuestionLabel}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ minWidth: 0 }}
            >
              · {questionRow.sourceQuestionTitle}
            </Typography>
          </Box>
        </Tooltip>
      )}

      {datasetMetricRow && (
        <Tooltip
          title={
            resolvedMetric?.source
              ? `${resolvedMetric.source.question.questionLabel} · ${resolvedMetric.source.question.questionTitle}`
              : ""
          }
        >
          <Box
            sx={{
              ml: CONTENT_INDENT,
              display: "flex",
              alignItems: "baseline",
              gap: 0.5,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              variant="caption"
              color="text.primary"
              fontWeight={500}
              sx={{ flexShrink: 0 }}
            >
              {resolvedMetric?.source
                ? resolvedMetric.source.question.questionLabel
                : (resolvedMetric?.name ?? datasetMetricRow.datasetMetricName)}
            </Typography>
            {resolvedMetric?.source && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ minWidth: 0 }}
              >
                · {resolvedMetric.source.question.questionTitle}
              </Typography>
            )}
            {!resolvedMetric && (
              <Tooltip title="This saved metric could no longer be found — reopen this row to repair it.">
                <Chip label="Unavailable metric" color="warning" size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
              </Tooltip>
            )}
            {resolvedMetric && metricIneligibility && !metricIneligibility.eligible && (
              <Tooltip title={metricIneligibility.reason ?? "This metric is no longer eligible for brand mapping."}>
                <Chip label="Needs attention" color="warning" size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
              </Tooltip>
            )}
          </Box>
        </Tooltip>
      )}

      {(questionRow || datasetMetricRow) && (
        <Box
          sx={{
            mt: 0.5,
            ml: CONTENT_INDENT,
            display: "flex",
            alignItems: "center",
            gap: "7px",
            flexWrap: "nowrap",
            overflowX: "auto",
            "& > *": { flexShrink: 0 },
          }}
        >
          <MetaLabel variant="source" label={questionRow ? "Question" : "Metric"} />
          <Tooltip
            title={
              questionRow?.rollup
                ? `${(questionRow ?? datasetMetricRow)!.resultType === "mean" ? "Mean" : "Percentage"} — Rollup: ${questionRow.rollup.name}`
                : (questionRow ?? datasetMetricRow)!.resultType === "mean"
                  ? "Mean"
                  : "Percentage"
            }
          >
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              <MetaLabel
                variant={(questionRow ?? datasetMetricRow)!.resultType === "mean" ? "mean" : "percentage"}
                label={(questionRow ?? datasetMetricRow)!.resultType === "mean" ? "Mean" : "Percentage"}
              />
            </span>
          </Tooltip>
          {questionRow &&
            (questionRow.rowBase ? (
              <Tooltip title={getRowBaseSummaryLabel(questionRow.rowBase)}>
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <MetaLabel
                    variant="base"
                    label={truncateLabel(
                      getRowBaseSummaryLabel(questionRow.rowBase),
                      MAX_BASE_TAG_LENGTH,
                    )}
                  />
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={getBaseSummaryLabel(questionRow)}>
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <MetaLabel
                    variant="base"
                    label={truncateLabel(getBaseSummaryLabel(questionRow), MAX_BASE_TAG_LENGTH)}
                  />
                </span>
              </Tooltip>
            ))}
          {questionRow?.rowBaseNeedsAttention && (
            <Chip
              label="Base needs attention"
              color="warning"
              size="small"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          )}
        </Box>
      )}

      {questionRow && (
        <BrandMappingsList
          brandMappings={questionRow.brandMappings}
          brandLabelById={brandLabelById}
          contentIndent={CONTENT_INDENT}
          getBaseStatus={
            questionRow.rowBase
              ? (brandId) => getBaseMappingForBrand(questionRow, brandId, allRows)?.status
              : undefined
          }
        />
      )}

      {datasetMetricRow && (
        <BrandMappingsList
          brandMappings={datasetMetricRow.brandMappings}
          brandLabelById={brandLabelById}
          contentIndent={CONTENT_INDENT}
        />
      )}
    </Box>
  );
};

export default RowListItem;
